import { AccessTokenMonitor } from '@common/context/auth0Context';
import Config from '@config';
import { useAirtableQuery } from '@effects/useAirtableQuery';
import { DeepPartial, IGlobalState } from '@typings';
import Airtable from 'airtable';
import React, { createContext, Dispatch, useContext, useReducer } from 'react';

import reducers, { actionTypes as allActionTypes, initialState } from './Reducers';

const base = new Airtable({ apiKey: Config.AIRTABLE_API_KEY }).base(Config.AIRTABLE_BASE_ID);

const fetchCustomConfiguration = async () => {
    try {
        if (process.env.NODE_ENV === 'production') {
            /**
             * TODO: the custom_configuration should be used only when NODE_ENV is production for now
             * but currently, NODE_ENV is set to production for all the remote environments
             */
            return new Map<string, string>();
        }

        const configs = await base('Custom_Configuration').select().all();

        return configs.reduce((acc, record) => {
            const name = record.get('Name');
            const value = record.get('Value');
            if (name) {
                acc.set(name as string, value as string);
            }
            return acc;
        }, new Map<string, string>());
    } catch (e) {
        console.error('Airtable fetch error:', e);
        return new Map<string, string>();
    }
};

export const actionTypes = allActionTypes;

const StateContext = createContext<[IGlobalState, Dispatch<any>]>([{} as IGlobalState, () => null]);
const DispatchContext = createContext<Dispatch<any>>(() => null);

interface Props {
    children: any;
    mockState?: DeepPartial<IGlobalState>;
}

export const StateProvider: React.FunctionComponent<Props> = ({ children, mockState }) => {
    const state: IGlobalState = (mockState as IGlobalState) || initialState;
    const value = useReducer(reducers, state);
    const { data, isLoading } = useAirtableQuery<Map<string, string>>(
        'customer_configuration',
        fetchCustomConfiguration
    );

    const threshholdInSeconds = parseInt(data?.get('AUTH0_TOKEN_REFRESH_BUFFER_SECONDS') || '');

    return (
        <StateContext.Provider value={value}>
            {!isLoading && <AccessTokenMonitor threshholdInSeconds={threshholdInSeconds} />}
            <DispatchContext.Provider value={value[1]}>{children}</DispatchContext.Provider>
        </StateContext.Provider>
    );
};

export const useGlobalState = () => {
    const stateContext = useContext(StateContext);
    return stateContext;
};

export const useGlobalDispatch = () => {
    const dispatchContext = useContext(DispatchContext);
    return dispatchContext;
};

export interface ActionType {
    type: string;
    value: any;
    payload: any;
}
